import { z } from 'zod'
import { streamFiltersSchema } from './stream'

// ── Library saved views ───────────────────────────────────────────────────────────────────────

export const COLUMN_IDS = [
  'headline',
  'mediaType',
  'link',
  'outlet',
  'author',
  'folder',
  'topic',
  'tag',
  'date',
  'sentiment',
  'classification',
  'reach',
  'snippet',
] as const
export type BuiltinColumnId = (typeof COLUMN_IDS)[number]
/** A column beyond the fixed row — a named field or the build's own column (stream-columns.ts). */
export type FieldColumnId = `field:${string}`
export const FIELD_COLUMN_PREFIX = 'field:'
export const columnIdSchema = z.union([
  z.enum(COLUMN_IDS),
  z.custom<FieldColumnId>(
    (value) =>
      typeof value === 'string' &&
      value.startsWith(FIELD_COLUMN_PREFIX) &&
      value.length > FIELD_COLUMN_PREFIX.length &&
      value.length <= 120,
    'A field column is "field:<name>"',
  ),
])
export type ColumnId = z.infer<typeof columnIdSchema>

export const sortStateSchema = z.object({
  id: columnIdSchema,
  dir: z.enum(['asc', 'desc']),
})
export type SortState = z.infer<typeof sortStateSchema>

/** A saved reading position: columns + sort + filters; restoring one swaps all three. */
export const libraryViewSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  filters: streamFiltersSchema,
  sort: sortStateSchema,
  columns: z.array(columnIdSchema),
  /** The view the Library opens on for this workspace; at most one per sub-account. */
  starred: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
})
export type LibraryView = z.infer<typeof libraryViewSchema>

export const createLibraryViewSchema = z.object({
  name: z.string().trim().min(1).max(120),
  filters: streamFiltersSchema,
  sort: sortStateSchema,
  columns: z.array(columnIdSchema).min(1).max(COLUMN_IDS.length),
  starred: z.boolean().default(false),
})
export type CreateLibraryView = z.infer<typeof createLibraryViewSchema>

export const updateLibraryViewSchema = createLibraryViewSchema.partial()
export type UpdateLibraryView = z.infer<typeof updateLibraryViewSchema>

export const listLibraryViewsResponseSchema = z.object({ views: z.array(libraryViewSchema) })
export type ListLibraryViewsResponse = z.infer<typeof listLibraryViewsResponseSchema>
